import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useActiveCall } from "@/features/call/context/ActiveCallContext";
import {
  deviceErrorMessageKey,
  type DeviceKind,
} from "@/features/call/utils/deviceErrors";

/**
 * Wraps a device action (mic / camera / screen-share toggle) so that a click
 * handler can call it directly:
 *
 *  - a rejected promise is caught and reported through the call toast instead
 *    of escaping as an unhandled rejection,
 *  - clicks that arrive while the previous action is still in flight are
 *    ignored, so a double-click cannot queue an enable behind a disable,
 *  - `busy` lets the button reflect the in-flight state.
 */
export const useDeviceAction = (
  kind: DeviceKind,
  action: () => Promise<unknown>,
) => {
  const { t } = useTranslation();
  const { showToast } = useActiveCall();
  const inFlightRef = useRef(false);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy(true);
    try {
      await action();
    } catch (err) {
      console.error(`[ActiveCall] ${kind} action failed:`, err);
      const key = deviceErrorMessageKey(err, kind);
      if (key) showToast(t(key), "error");
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  }, [action, kind, showToast, t]);

  return { run, busy };
};
